import {
  Alert as AlertChakra,
  AlertIcon,
  Box,
  Button as ButtonChakra,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Switch,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Alert, Form, message, Popconfirm, Space, Table } from "antd";
import {
  Filter,
  Info,
  Plus,
  Search,
  SquarePenIcon,
  TrashIcon,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SCREEN_VERTICAL_SPACING } from "../../../constants";
import { DBAction } from "../../../enum/DBAction";
import { StoreState } from "../../../enum/StoreState";
import {
  AlertModule,
  IOperationRuleAlertForTable,
  SensorAlertType,
} from "../../../stores/data/insightAlerts/insightAlertModel";
import { useStores } from "../../../stores/setup/use-store";
import { IStation } from "../../../stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import AlertEditDrawer from "./Components/AlertEditDrawer";
import AlertCreateDrawer from "../../Alert/AlertCreateDrawer";
import { PhoneVerificationModal } from "../../Admin/MyData/components/PhoneVerificationModal";
import { useAlertPhoneVerification } from "../../Alert/hooks/useAlertPhoneVerification";

import { AlertFiltersDrawer } from "./Components/AlertFiltersDrawer";
import { AlertTutorialModal } from "./Components/AlertTutorialModal";
import { useLocation, useHistory } from "react-router-dom";

/**
 * Spec 043 — virtual table row representing a group of OperationRuleAlert
 * rows tied to the same event. The grouped row exposes the underlying ids
 * (`_groupRowIds`) and a combined type list (`_groupTypes`) so trash/edit
 * actions and the "Tipo" column can target the entire group.
 */
interface IGroupedAlertRow extends IOperationRuleAlertForTable {
  _groupRowIds: number[];
  _groupTypes: SensorAlertType[];
}

const InsightAlertDetail: React.FC = () => {
  const {
    dataStores: { insightAlertStore, authStore, forecastChartPageStore },
  } = useStores();

  const [form] = Form.useForm();

  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightAlert",
  });
  const { i18n } = useTranslation("translation");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isFilterOpen,
    onOpen: onFilterOpen,
    onClose: onFilterClose,
  } = useDisclosure();

  const {
    isOpen: isAlertTutorialOpen,
    onOpen: onAlertTutorialOpen,
    onClose: onAlertTutorialClose,
  } = useDisclosure();

  const {
    isOpen: isPhoneModalOpen,
    onOpen: onPhoneModalOpen,
    onClose: onPhoneModalClose,
    phoneData,
    handlePhoneUpdateSuccess,
    isPhoneAlreadyVerified,
    t: tPhoneModal,
  } = useAlertPhoneVerification();

  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Check if we should open the create drawer (returning from Forecast Charts)
  const shouldOpenCreateDrawer =
    queryParams.get("open_create_drawer") === "true";

  // Check if filtering by insight (coming from OperationRuleLocationInsightDetail)
  const isFilteringByInsight = !!queryParams.get(
    "operationRuleLocationItemIds",
  );

  // Use state for create drawer, initialized based on query param
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(
    shouldOpenCreateDrawer,
  );

  // Clean up URL after reading query param
  useEffect(() => {
    if (shouldOpenCreateDrawer) {
      history.replace("/insight-alerts");
    }
  }, [shouldOpenCreateDrawer, history]);

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const alertId = queryParams.get("alert_id");
    const returningFromSelection =
      queryParams.get("returning_from_selection") === "true";

    // Get station data from URL if returning from map selection
    const selectedLat = queryParams.get("selected_lat");
    const selectedLon = queryParams.get("selected_lon");
    const selectedStationId = queryParams.get("selected_station_id");
    const selectedStationName = queryParams.get("selected_station_name");

    if (alertId) {
      const id = parseInt(alertId);
      if (!isNaN(id)) {
        if (returningFromSelection) {
          if (selectedLat && selectedLon) {
            const lat = parseFloat(selectedLat);
            const lon = parseFloat(selectedLon);
            const stationId = selectedStationId
              ? parseInt(selectedStationId)
              : 0;
            const stationName = selectedStationName
              ? decodeURIComponent(selectedStationName)
              : `${lat.toFixed(4)}, ${lon.toFixed(4)}`;

            if (!isNaN(lat) && !isNaN(lon)) {
              const restoredStation: IStation = {
                station_id: stationId,
                station_name: stationName,
                station_lat: lat,
                station_lon: lon,
                station_depth: 0,
                station_depth_unit: "m",
                station_save_by_user: false,
              };
              insightAlertStore.setSelectedStationForAlert(restoredStation);
              insightAlertStore.setAlertLocationType("point");
            }
          }

          history.replace(`/insight-alerts?alert_id=${id}`);

          // Always fetch alert data from API - the useAlertEditDrawer hook will
          // prioritize selectedGeofence over the API geofence when displaying
          if (!insightAlertStore.operationRuleAlert) {
            insightAlertStore.getOperationRuleAlertAPI(id);
            insightAlertStore.getOperationRuleAlertUserAPI({
              operationRuleAlertId: id,
              lang: i18n?.language,
            });
          }
        } else {
          // Only fetch the alert if it's not already loaded (avoid resetting user's changes)
          const currentAlertId = insightAlertStore.operationRuleAlert?.id;
          if (currentAlertId !== id) {
            insightAlertStore.setOperationRuleAlert(null);
            insightAlertStore.getOperationRuleAlertAPI(id);
            insightAlertStore.setOperationRuleAlertAction(DBAction.UPDATE);
            insightAlertStore.getOperationRuleAlertUserAPI({
              operationRuleAlertId: id,
              lang: i18n?.language,
            });
            insightAlertStore.setSelectedAlertModule(AlertModule.NEW);
          }
        }
        onOpen();
      }
    }
  }, []);

  useEffect(() => {
    if (insightAlertStore.operationRuleDetailSelected === 0) {
      form.resetFields();
      const operationRuleLocationItemIdsParam = queryParams.get(
        "operationRuleLocationItemIds",
      );
      const operationRuleLocationItemIds = operationRuleLocationItemIdsParam
        ? operationRuleLocationItemIdsParam
            .split(",")
            .map(Number)
            .filter((n) => !isNaN(n))
        : undefined;

      if (operationRuleLocationItemIds) {
        insightAlertStore.setIsFilterApplied(true);
      }

      insightAlertStore.getOperationRuleAlertForTableAPI({
        operationRuleLocationItemIds,
      });

      insightAlertStore.getOperationRuleAPI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightAlertStore.operationRuleDetailSelected]);

  const handleOnCreateCustomAlert = () => {
    insightAlertStore.setCreateFormValues(null);
    insightAlertStore.setSelectedGeofence([]);
    insightAlertStore.setSelectedStationForAlert(null);
    setIsCreateDrawerOpen(true);
  };

  const handleCreateDrawerClose = () => {
    const isInSelectionMode =
      forecastChartPageStore.isAreaDrawingMode ||
      forecastChartPageStore.isPointSelectionMode;

    setIsCreateDrawerOpen(false);

    if (!isInSelectionMode) {
      insightAlertStore.setCreateFormValues(null);
      insightAlertStore.setSelectedGeofence([]);
      insightAlertStore.setSelectedStationForAlert(null);
    }
  };

  const handleOnConfirmDelete = (record: IGroupedAlertRow) => {
    // Spec 043 — when the row represents an event group (rain + lightning),
    // delete the whole group transactionally via the event endpoint. Legacy
    // standalone rows fall back to the single-row OperationRuleAlert delete.
    if (record.operationRuleLocationItemId) {
      insightAlertStore.deleteAlertGroupViaEventAPI(
        record.operationRuleLocationItemId,
      );
    } else if (record.id > 0) {
      insightAlertStore.deleteOperationRuleAlertAPI(record.id);
    } else {
      message.error(t("alertRemovalFailed"));
    }
  };

  const handleOnEdit = (value: IGroupedAlertRow) => {
    // Clear selectedGeofence when starting a new edit to avoid stale data
    insightAlertStore.setSelectedGeofence([]);
    insightAlertStore.setSelectedStationForAlert(null);
    insightAlertStore.setAlertLocationType(null);
    insightAlertStore.setIsGeofenceSavedToLocation(false);
    insightAlertStore.setAreaSelectorVisible(false);

    insightAlertStore.setOperationRuleAlert(null);
    insightAlertStore.setOperationRuleAlertAction(DBAction.UPDATE);
    insightAlertStore.setOperationRuleAlertForTableForEdit(value);

    // Always use AlertEditDrawer for both insight alerts and standalone alerts
    insightAlertStore.setSelectedAlertModule(AlertModule.NEW);
    onOpen();
    // Spec 043 — load every row of the group; the store merges the rain
    // sensors + lightning slice into a single virtual operationRuleAlert
    // so the drawer renders both tabs from a single source.
    insightAlertStore.getOperationRuleAlertGroupAPI(value._groupRowIds);
    insightAlertStore.getOperationRuleAlertUserAPI({
      operationRuleAlertId: value?.id,
      lang: i18n?.language,
    });
  };

  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return insightAlertStore.operationRuleAlertForTableDatail
      .filter((record) => {
        const alertType = insightAlertStore.alertTypes.find(
          (alertType) => alertType == record.alertType,
        );
        const alertTypeName = alertType
          ? t(alertType)
          : t("createdFromScratch");

        return (
          record.name?.toLowerCase().includes(term) ||
          alertTypeName?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => b.id - a.id);
  }, [
    searchTerm,
    insightAlertStore.operationRuleAlertForTableDatail,
    insightAlertStore.alertTypes,
  ]);

  /**
   * Spec 043 — collapse OperationRuleAlert rows tied to the same event into
   * a single visual row. Standalone rows (no event link) keep their own
   * line. The grouped row carries `_groupRowIds` so edit/delete actions can
   * target every underlying row, and `_groupTypes` to render the combined
   * "Tipo" column (e.g. "Chuva + Raio").
   */
  const groupedData: IGroupedAlertRow[] = useMemo(() => {
    const groups = new Map<string, IOperationRuleAlertForTable[]>();
    for (const row of filteredData) {
      const key = row.operationRuleLocationItemId
        ? `event-${row.operationRuleLocationItemId}`
        : `row-${row.id}`;
      const arr = groups.get(key) ?? [];
      arr.push(row);
      groups.set(key, arr);
    }
    const built = Array.from(groups.values()).map((rows) => {
      // Prefer the rain row as the visual primary — it carries the canonical
      // sensor list. Falls back to the first row when only lightning exists.
      const primary =
        rows.find((r) => (r.type ?? "sensor") !== "lightning") ?? rows[0];
      const types = rows.map((r) => (r.type ?? "sensor") as SensorAlertType);
      return {
        ...primary,
        _groupRowIds: rows.map((r) => r.id),
        _groupTypes: types,
      };
    });
    // Sort by the smallest row id in the group (the original creation id).
    // Using that key keeps the grouped row in a stable position across edits
    // — adding a sibling row (e.g. a new rain row to a lightning-only event)
    // creates a high id but doesn't change the minimum, so the alert stays
    // where the user expects it instead of jumping to the top.
    return built.sort(
      (a, b) =>
        Math.min(...b._groupRowIds) - Math.min(...a._groupRowIds),
    );
  }, [filteredData]);

  const handleSelectedDashboardChange = (value: number) => {
    if (value !== insightAlertStore.operationRuleDetailSelected) {
      insightAlertStore.setOperationRuleDetailSelected(value);
      form.setFieldsValue({
        selectLocation: 0,
      });
      insightAlertStore.setOperationRuleLocationDetailSelected(0);
    }
    if (value > 0) {
      insightAlertStore.getOperationRuleLocationAPI(value);
      insightAlertStore.getOperationRuleAlertForTableAPI({
        operationRuleId: value,
      });
    } else {
      insightAlertStore.getOperationRuleAlertForTableAPI({});
    }
  };

  const handleSelectedLocationChange = (value: number) => {
    if (insightAlertStore.operationRuleDetailSelected > 0) {
      if (value > 0) {
        insightAlertStore.setOperationRuleLocationDetailSelected(value);
        insightAlertStore.getOperationRuleAlertForTableAPI({
          operationRuleId: insightAlertStore.operationRuleDetailSelected,
          operationRuleLocationId: value,
        });
      } else {
        insightAlertStore.getOperationRuleAlertForTableAPI({
          operationRuleId: insightAlertStore.operationRuleDetailSelected,
        });
      }
    } else {
      message.warning(t("selectDashboardFilterLocation"));
    }
  };

  const handleSelectedAlertTypeChange = (value: string) => {
    insightAlertStore.getOperationRuleAlertForTableAPI({
      alertType: value,
    });
  };

  const columns = [
    {
      title: t("alertTitleColumn"),
      key: "Alert",
      dataIndex: "Alert",
      width: "25%",
      render: (_: React.ReactNode, record: IOperationRuleAlertForTable) => (
        <h3 style={{ color: "#71717A", margin: 0 }}>
          {record.notifications?.[0]?.title || record.name}
        </h3>
      ),
    },
    {
      title: t("eventColumn"),
      key: "Event",
      dataIndex: "Event",
      width: "20%",
      render: (_: React.ReactNode, record: IOperationRuleAlertForTable) => (
        <h3 style={{ color: "#71717A", margin: 0 }}>
          {record.operationRuleLocationItemName || "-"}
        </h3>
      ),
    },
    {
      title: t("alertType"),
      key: "AlertType",
      dataIndex: "AlertType",
      width: "20%",
      render: (_: React.ReactNode, record: IOperationRuleAlertForTable) => {
        const alertType = record.alertType;
        return (
          <h3 style={{ color: "#71717A", margin: 0 }}>
            {alertType ? t(alertType) : t("createdFromScratch")}
          </h3>
        );
      },
    },
    {
      // Spec 043 FR-010: "Tipo" column. With grouped rows the column now
      // shows every type configured under the group (e.g. "Chuva + Raio").
      // Use i18n.t to bypass the parent useTranslation keyPrefix.
      title: i18n.t("oceanWeatherIntelligence.sensorAlert.tipoColumn"),
      key: "Type",
      dataIndex: "type",
      width: "10%",
      render: (_: React.ReactNode, record: IGroupedAlertRow) => {
        const types = record._groupTypes ?? [record.type ?? "sensor"];
        const uniqueLabels = Array.from(
          new Set(
            types.map((tp) =>
              tp === "lightning"
                ? i18n.t("oceanWeatherIntelligence.sensorAlert.raioLabel")
                : i18n.t("oceanWeatherIntelligence.sensorAlert.chuvaLabel"),
            ),
          ),
        );
        return (
          <h3 style={{ color: "#71717A", margin: 0 }}>
            {uniqueLabels.join(" + ")}
          </h3>
        );
      },
    },
    {
      title: t("locationColumn"),
      key: "Location",
      dataIndex: "Location",
      width: "15%",
      render: (_: React.ReactNode, record: IOperationRuleAlertForTable) => {
        const locationName = record.operationRuleLocationName || record.area;
        return (
          <h3 style={{ color: "#71717A", margin: 0 }}>{locationName || "-"}</h3>
        );
      },
    },
    {
      title: t("active"),
      key: "active",
      dataIndex: "active",
      width: "5%",
      render: (text: React.ReactNode, record: IGroupedAlertRow) => {
        return (
          <Switch
            defaultChecked={!!record.active}
            colorScheme={"blue"}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const isChecked = event.target.checked;
              // Spec 043 — toggle propagates to every row in the group so
              // the unified UX matches the underlying state across rain
              // and lightning siblings.
              record._groupRowIds.forEach((rowId) =>
                insightAlertStore.setActive(rowId, isChecked),
              );
            }}
          />
        );
      },
    },
    {
      title: "",
      dataIndex: "action",
      width: "5%",
      render: (text: React.ReactNode, record: IGroupedAlertRow) => {
        return (
          <Space>
            <>
              <SquarePenIcon
                type="primary"
                style={{
                  margin: "0 8px",
                  color: "#71717A",
                  cursor: "pointer",
                }}
                onClick={() => handleOnEdit(record)}
                size="20px"
              >
                {t("edit")}
              </SquarePenIcon>
              {!authStore.readOnlyUser && (
                <Popconfirm
                  placement="top"
                  title={t("areYouSureWantDeleteThisAlert")}
                  onConfirm={(e) => handleOnConfirmDelete(record)}
                  okText={t("yes")}
                  cancelText={t("no")}
                >
                  <TrashIcon
                    type="default"
                    style={{
                      margin: "0 8px",
                      cursor: "pointer",
                      color: "#71717A",
                    }}
                    size="20px"
                  >
                    {t("delete")}
                  </TrashIcon>
                </Popconfirm>
              )}
            </>
          </Space>
        );
      },
    },
  ];

  return (
    <Box mx={"40px"} my={SCREEN_VERTICAL_SPACING}>
      <Text fontSize="3xl" color="black">
        {t("insightAlerts")}
      </Text>
      <Text fontSize="md" color="#3A3A3A">
        {t("createAndCustomizeAlert")}
      </Text>

      {insightAlertStore.state === StoreState.ERROR ? (
        <Alert
          message="Error"
          description={insightAlertStore.messageError}
          type="error"
          showIcon
          closable
        />
      ) : (
        ""
      )}
      {authStore.readOnlyUser && (
        <AlertChakra bg="#bee3f8" status="info" mb={4} color="gray.700">
          <AlertIcon />
          {i18n.t("readingModePleaseContact")}
        </AlertChakra>
      )}
      <AlertEditDrawer onClose={onClose} isOpen={isOpen} />
      <AlertTutorialModal
        isOpen={isAlertTutorialOpen}
        onClose={onAlertTutorialClose}
      />
      <AlertFiltersDrawer
        isOpen={isFilterOpen}
        onClose={onFilterClose}
        onLocationChange={handleSelectedLocationChange}
        onDashboardChange={handleSelectedDashboardChange}
        onAlertTypeChange={handleSelectedAlertTypeChange}
      />
      <Flex alignItems={"center"} justifyContent={"space-between"} mt="24px">
        <Flex gap={"12px"}>
          <InputGroup width="400px">
            <InputLeftElement
              pointerEvents="none"
              children={<Search size="15px" color="#717680" />}
            />
            <Input
              id="searchField"
              color="#3A3A3A"
              bg="white"
              width="400px"
              placeholder={t("searchByAlert") as string}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Flex alignItems={"center"} gap={3}>
            {!isFilteringByInsight && (
              <Box position="relative" display="inline-block">
                {insightAlertStore.isFilterApplied && (
                  <Box
                    position="absolute"
                    top="-6px"
                    right="-6px"
                    bg="red"
                    w="16px"
                    h="16px"
                    borderRadius="full"
                    zIndex={1}
                  />
                )}
                <ButtonChakra
                  layerStyle="oceanOutline"
                  _hover={{ layerStyle: "oceanOutline" }}
                  onClick={onFilterOpen}
                  leftIcon={<Filter size="16px" />}
                >
                  {t("filterList")}
                </ButtonChakra>
              </Box>
            )}
            {insightAlertStore.isFilterApplied && (
              <Box position="relative" display="inline-block">
                {isFilteringByInsight && (
                  <Box
                    position="absolute"
                    top="-6px"
                    right="-6px"
                    bg="red"
                    w="16px"
                    h="16px"
                    borderRadius="full"
                    zIndex={1}
                  />
                )}
                <Text
                  color="newBlue.500"
                  mr={3}
                  cursor="pointer"
                  textDecoration={"underline"}
                  onClick={() => {
                    insightAlertStore.clearFilters();
                    history.replace("/insight-alerts");
                  }}
                >
                  {t("clear")}
                </Text>
              </Box>
            )}
          </Flex>
        </Flex>

        <ButtonChakra
          colorScheme="blue"
          leftIcon={<Plus />}
          bg="#086AA9"
          _hover={{ bg: "rgba(40, 103, 149, 0.8)" }}
          onClick={handleOnCreateCustomAlert}
        >
          <Text mt="3px">{t("createNewAlert")}</Text>
        </ButtonChakra>
      </Flex>

      <AlertChakra
        status="info"
        bg="#DBEAFE"
        color="#1A3478"
        borderRadius={"6px"}
        mb={4}
        mt="18px"
        gap={"16px"}
      >
        <Info size="18px" />
        <Flex mt="4px">
          <Text mr={1}>{t("weVeRedesigned")}</Text>
          <Text
            textDecoration={"underline"}
            cursor="pointer"
            onClick={() => onAlertTutorialOpen()}
          >
            {t("clickToLearnMore")}
          </Text>
        </Flex>
      </AlertChakra>

      <Table
        columns={columns}
        dataSource={groupedData}
        rowKey={(record) => record._groupRowIds.join("-")}
        key={`item${insightAlertStore.operationRuleAlertForTableDatail.length}-${insightAlertStore.alertTypes.length}`}
        loading={insightAlertStore.state === StoreState.PEDDING}
        style={{ marginTop: "18px" }}
      ></Table>

      <AlertCreateDrawer
        isOpen={isCreateDrawerOpen}
        onClose={handleCreateDrawerClose}
        showPhoneVerificationModal={!isPhoneAlreadyVerified}
        onPhoneModalOpen={onPhoneModalOpen}
      />

      <PhoneVerificationModal
        isOpen={isPhoneModalOpen}
        onClose={onPhoneModalClose}
        onSuccess={handlePhoneUpdateSuccess}
        initialPhoneData={phoneData}
        title={tPhoneModal("updatePhoneNumber") as string}
        description={tPhoneModal("verifyPhoneToReceiveAlerts") as string}
        confirmButtonText={tPhoneModal("sendVerificationCode") as string}
        isPhoneAlreadyVerified={isPhoneAlreadyVerified}
      />
    </Box>
  );
};

export default observer(InsightAlertDetail);
